import React from "react";
import {
  Card,
  CardBody,
  CardFooter,
  Button,
  Badge,
  Chip,
  addToast,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import { Order } from "@/backend/orders/domain/order";
import { useOrders } from "@/backend/orders/infrastructura/useOrders";
import Link from "next/link";

interface OrderCardProps {
  order: Order;
  completed?: boolean;
}

export const OrderCard: React.FC<OrderCardProps> = ({ order, completed }) => {
  const { updateOrder } = useOrders();
  const handleClick = (id: string) => {
    addToast({
      title: "Completar pedido",
      promise: updateOrder({ id, order: { status: "COMPLETED" } }),
    });
  };

  return (
    <Card className="w-full bg-gradient-to-br from-white to-gray-50 border border-gray-200 hover:border-primary hover:shadow-md transition-all">
      <CardBody className="p-5">
        {/* Header con ID y Precio */}
        <div className="flex justify-between items-start gap-2 mb-5">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-gray-500 font-medium mb-1">Orden</p>
            <p className="text-xl font-bold text-gray-900 truncate">{order.referenceId || order.id.slice(0, 8)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500 font-medium mb-1">{order.divisa}</p>
            <p className="text-xl font-bold text-primary">{order.price}</p>
          </div>
        </div>

        {/* Información secundaria */}
        <div className="space-y-3">
          {/* Método de entrega */}
          <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-100">
            <Icon
              icon={order.deliveryMethod === "SHIPPING" ? "lucide:truck" : "lucide:hand-pickup"}
              className="text-primary text-lg flex-shrink-0"
            />
            <span className="text-sm font-medium text-gray-700">
              {order.deliveryMethod === "SHIPPING" ? "Envío a domicilio" : "Recogida en tienda"}
            </span>
          </div>

          {/* Fecha */}
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Icon icon="lucide:calendar" className="text-gray-400" />
            <span>{new Date(order.createdAt).toLocaleDateString("es-ES", { weekday: "short", month: "short", day: "numeric" })}</span>
          </div>
        </div>
      </CardBody>

      <CardFooter className="p-5 pt-0">
        <Button
          as={Link}
          href={`/shopper/?id=${order.id}`}
          color="primary"
          variant="flat"
          size="sm"
          fullWidth
          className="font-semibold"
          endContent={<Icon icon="lucide:arrow-right" />}
          onMouseDown={() => updateOrder({ id: order.id, order: { status: "IN_SHOP" } })}
          onTouchStart={() => updateOrder({ id: order.id, order: { status: "IN_SHOP" } })}
        >
          Procesar
        </Button>
      </CardFooter>
    </Card>
  );
};
